import React, { useEffect, useState } from "react";
import "./user.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  themeBackgroundKeyForViewport,
  themeGameBackgroundKeyForViewport,
  getDvSourceImageUrl,
  toCssUrlValue,
  normalizeThemeHexColor,
} from "../../functions/themeAssets";
import { ensureStageSessionForUser } from "../../functions/stageReportSync";
import { useLocation, useNavigate } from "react-router-dom";

/** Player shell: header (company logo + Back) and the theme page background. */
function USER({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { companyLogoUrl, backButtonUrl } = useSelector((state) => state.ui);
  const [logoUrl, setLogoUrl] = useState("");
  const [backUrl, setBackUrl] = useState("");

  const { user } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData, currentTheme } = useSelector((state) => state.theme);
  const fromMobileAppInUrl =
    String(new URLSearchParams(location.search).get("fromMobileApp") || "").toLowerCase() ===
    "true";
  const fromMobileAppInUser = String(user?.fromMobileApp || "").toLowerCase() === "true";
  const persistedFromMobileApp = (() => {
    try {
      const persisted = JSON.parse(sessionStorage.getItem("userData") || "{}");
      return String(persisted?.fromMobileApp || "").toLowerCase() === "true";
    } catch {
      return false;
    }
  })();
  const isFromMobileApp = fromMobileAppInUrl || fromMobileAppInUser || persistedFromMobileApp;
  const isGameRoute = /^\/game(?:\/|$)/.test(location.pathname);

  useEffect(() => {
    if (!/^\/login(?:\/)?$/.test(location.pathname)) {
      if (themeStatus === "idle") {
        dispatch(fetchThemeData({ themeId: currentTheme || null }));
      }
    }
  }, [themeStatus, dispatch, location.pathname, currentTheme]);

  useEffect(() => {
    try {
      const storedUser = user || JSON.parse(sessionStorage.getItem("userData") || "{}");
      ensureStageSessionForUser(storedUser);
    } catch {
      /* ignore */
    }
  }, [user?.userId, user?.userid, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBackUrl(backButtonUrl);
    setLogoUrl(companyLogoUrl);
  }, [backButtonUrl, companyLogoUrl]);

  // background_desk/mob on login and rules, game_desk/mob on the game page (old/*.php body styles).
  useEffect(() => {
    if (themeStatus !== "succeeded" || !themeData) return undefined;

    const applyBodyBackground = () => {
      const body = document.body;
      const bg = isGameRoute
        ? themeGameBackgroundKeyForViewport(themeData)
        : themeBackgroundKeyForViewport(themeData);
      const bgUrl = bg ? getDvSourceImageUrl(bg) : null;
      if (bgUrl) {
        body.style.backgroundImage = toCssUrlValue(bgUrl);
        body.style.backgroundSize = "100% 100%";
        body.style.backgroundPosition = "center center";
        body.style.backgroundRepeat = "no-repeat";
        // Fixed bg on desktop; scroll on portrait phones (fixed breaks iOS pan).
        const mobilePortrait = window.matchMedia(
          "(max-width: 900px) and (orientation: portrait)"
        ).matches;
        const needsScrollBg =
          mobilePortrait && /^\/(?:rules|game)(?:\/|$)/.test(location.pathname);
        body.style.backgroundAttachment = needsScrollBg ? "scroll" : "fixed";
      } else {
        body.style.removeProperty("background-image");
      }
    };

    applyBodyBackground();
    window.addEventListener("resize", applyBodyBackground);
    window.addEventListener("orientationchange", applyBodyBackground);
    return () => {
      window.removeEventListener("resize", applyBodyBackground);
      window.removeEventListener("orientationchange", applyBodyBackground);
    };
  }, [themeStatus, themeData, location.pathname, isGameRoute]);

  // Admin rules-page button colours, used by every player button (see truthDare.css .td-cta).
  useEffect(() => {
    const root = document.documentElement;
    const vars = {
      "--td-btn-bg": normalizeThemeHexColor(themeData?.button_color),
      "--td-btn-fg": normalizeThemeHexColor(themeData?.button_text_color),
    };
    Object.entries(vars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    });
  }, [themeData?.button_color, themeData?.button_text_color]);

  const handleLogoClick = (e) => {
    if (!isFromMobileApp) return;
    e.preventDefault();
    window.GameStatus?.postMessage?.("navigate_back");
  };

  const handleBackClick = (e) => {
    if (isGameRoute) {
      e.preventDefault();
      navigate("/rules");
      return;
    }
    if (!backUrl) {
      e.preventDefault();
      return;
    }
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (backUrl === current) {
      e.preventDefault();
    }
  };

  const backHref = isGameRoute ? "/rules" : backUrl || "#";

  return (
    <>
      <header className="upperaction">
        <a href={process.env.REACT_APP_BASE_URL} onClick={handleLogoClick}>
          <img src={logoUrl} className="logo-holder" alt="" />
        </a>
        {!isFromMobileApp && (
          <div className="back-holder">
            <a href={backHref} onClick={handleBackClick}>
              <button type="button" className="back-default">
                Back
              </button>
            </a>
          </div>
        )}
      </header>
      {children}
    </>
  );
}

export default USER;
